import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Globe, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useTheme } from "@/theme/ThemeContext";

export const ThemeToggle = ({ compact = false }) => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const next = isDark ? "light" : "dark";
  return (
    <button
      onClick={() => setTheme(next)}
      className={`relative w-9 h-9 ${compact ? "" : "sm:w-10 sm:h-10"} rounded-full glass flex items-center justify-center hover:border-[var(--accent)]/40 transition`}
      aria-label="Toggle theme"
      data-testid="theme-toggle"
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0, scale: 0.7 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: 0.7 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {isDark ? <Sun size={16} className="text-[var(--accent)]" /> : <Moon size={16} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
};

export const LanguageToggle = () => {
  const { lang, setLang, dict } = useI18n();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-9 sm:h-10 px-3 sm:px-3.5 rounded-full glass flex items-center gap-1.5 hover:border-[var(--accent)]/40 transition text-sm font-medium"
        aria-label="Choose language"
        data-testid="language-toggle"
      >
        <Globe size={14} className="text-[var(--accent)]" />
        <span className="font-semibold tabular-nums">{dict.langShort}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full right-0 mt-4 w-[220px] max-w-[calc(100vw-1rem)] rounded-xl p-1.5 shadow-xl z-50 backdrop-blur-xl ${
              theme === "dark"
                ? "border border-white/20 bg-[rgba(15,18,28,0.92)]"
                : "glass-strong border border-[var(--border-strong)]"
            } sm:!glass sm:!border-[var(--border)] sm:!bg-transparent`}
            data-testid="language-dropdown"
          >
            {[
              { id: "ro", short: "RO", name: "Română" },
              { id: "en", short: "EN", name: "English" },
              { id: "ua", short: "UA", name: "Українська" },
            ].map((l) => {
              const active = lang === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => { setLang(l.id); setOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between gap-2 transition ${
                    theme === "dark" ? "text-[rgba(255,255,255,0.95)] hover:bg-[var(--accent)]/20" : "text-[var(--text)] hover:bg-[var(--accent)]/12"
                  } ${active ? "bg-[var(--accent)]/22" : ""}`}
                  data-testid={`language-option-${l.id}`}
                >
                  <span className="flex items-center gap-2.5 text-sm">
                    <span className="font-semibold tabular-nums w-6 inline-block">{l.short}</span>
                    <span>{l.name}</span>
                  </span>
                  {active && <Check size={14} className="text-[var(--accent)]" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
